import { useQuery, useMutation } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { useLinkToken } from "@/pages/ResetPassword";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

// "Stop these scheduled updates" in a founder's scheduled investor update (backend doc_schedules.py): a public page,
// no sign-in. The link's token comes after "#", so it never reaches a server's logs; nothing stops before the person
// confirms here (a mail app's link check opens links, and must not stop anything).
export default function Optout() {
  const token = useLinkToken();
  const status = useQuery({ queryKey: ["optout", token], enabled: !!token, retry: false,
                            queryFn: async () => (await api.post("/optout/status", { token })).data });
  const stop = useMutation({ mutationFn: async () => (await api.post("/optout", { token })).data });
  const company = status.data?.company || "This company";
  if (!token || status.isError) return (
    <AuthShell title="This link doesn't work" subtitle={token ? apiErr(status.error) : "Open the link from the email again."} />
  );
  if (!status.data) return <AuthShell title="Scheduled updates"><Loader2 className="w-5 h-5 animate-spin" /></AuthShell>;
  if (status.data.stopped || stop.isSuccess) return (
    <AuthShell title="Stopped" subtitle={`${company}'s scheduled updates won't come to you anymore.`} />
  );
  return (
    <AuthShell title={`Stop ${company}'s scheduled updates?`}
      subtitle={`You get these updates by email because ${company} added you. Stop them, and that schedule sends you nothing more.`}>
      <Button className="w-full" disabled={stop.isPending} onClick={() => stop.mutate()} data-testid="optout-stop">
        {stop.isPending && <Loader2 className="w-4 h-4 animate-spin" />}Stop these updates
      </Button>
      {stop.isError && <p className="text-sm text-gr-danger mt-3" role="alert">{apiErr(stop.error)}</p>}
    </AuthShell>
  );
}
